import React, { useEffect } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { auth } from "../firebase";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Link,
  Divider,
  useTheme,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Chip,
  Toolbar,
  Stack,
} from "@mui/material";
import { TypeAnimation } from "react-type-animation";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import TwitterIcon from "@mui/icons-material/Twitter";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TagIcon from "@mui/icons-material/LocalOffer";
import SearchIcon from "@mui/icons-material/Search";
import BarChartIcon from "@mui/icons-material/BarChart";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Helmet } from "react-helmet";
import CancelIcon from "@mui/icons-material/Cancel";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { alpha } from "@mui/material/styles";
import animationData from "../assets/welcome-animation.json";
import Lottie from "lottie-react";

const Reveal = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 0.6, ease: "easeOut", delay }}
  >
    {children}
  </motion.div>
);

const faqs = [
  {
    q: "Do I need a credit card to start my free trial?",
    a: "No credit card required — just sign up and enjoy 14 days free. Cancel anytime, no strings attached.",
  },
  {
    q: "Can I use SortlyBooks on my phone?",
    a: "Yes! Our iPhone app is live, so you can snap receipts, send invoices, and check your dashboard on the go. Android coming soon.",
  },
  {
    q: "What can I upload or track?",
    a: "You can upload receipts (PDF, image, or email), send branded invoices, and track profits by project or client — all from one place.",
  },
  {
    q: "Is my data private and secure?",
    a: "Absolutely. Your data lives on secure EU servers, fully GDPR-compliant. Only you control access — not even your accountant unless you invite them.",
  },
  {
    q: "What happens after my trial?",
    a: "You’ll be invited to upgrade. If you don’t, your data is safely stored and you can access it anytime by upgrading later — no hidden traps.",
  },
  {
    q: "Can my accountant access my reports?",
    a: "Yes — with your permission, they can receive monthly reports or even view your dashboard directly. You stay in control.",
  },
  {
    q: "How fast is support?",
    a: "We reply within 1 business day (often much faster). Real people, not bots. Just email support@sortlybooks.com.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.a,
    },
  })),
};

export default function Home() {
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((user) => {
      if (user) navigate("/dashboard");
    });
    return unsub;
  }, [navigate]);

  const features = [
    {
      icon: <TagIcon />,
      title: "Auto-tag invoices & receipts",
      bg: "#dcfce7",
    },
    {
      icon: <SearchIcon />,
      title: "Smart search across all documents",
      bg: "#ede9fe",
    },
    {
      icon: <BarChartIcon />,
      title: "Monthly accountant-ready reports",
      bg: "#fef9c3",
    },
    {
      icon: <ReceiptIcon />,
      title: "Send branded PDF invoices in 1 click",
      bg: "#dbeafe",
    },
  ];

  const testimonials = [
    {
      quote: "SortlyBooks saved me hours of admin. My accountant loves it.",
      name: "— Marta L., Graphic Designer",
    },
    {
      quote: "I just snap receipts and forget about it. Huge time saver.",
      name: "— Tom R., Shopify Store Owner",
    },
    {
      quote: "Now I can send invoices and track receipts in one place.",
      name: "— Liam K., Videographer",
    },
  ];

  return (
    <>
      <Helmet>
        <title>SortlyBooks – Receipts, Invoices & Payroll on Autopilot</title>
        <meta
          name="description"
          content="SortlyBooks automates your freelancer admin — upload receipts, send invoices, track payroll, and auto-send monthly reports to your accountant. Try it free – no credit card needed."
        />
        <meta
          property="og:title"
          content="SortlyBooks – Receipts, Invoices & Payroll on Autopilot"
        />
        <meta
          property="og:description"
          content="Upload receipts, send invoices, track payroll, and deliver monthly reports automatically. Trusted by freelancers across Europe."
        />
        <meta
          property="og:image"
          content="https://www.sortlybooks.com/og-preview.png"
        />
        <meta property="og:url" content="https://www.sortlybooks.com" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Box>
        <Navbar />
        <Toolbar /> {/* Spacer to offset fixed navbar */}
        {/* HERO */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            minHeight: "80vh",
            px: { xs: 2, md: 8 },
            pt: { xs: 6, md: 12 },
            bgcolor: "#F8FAFC",
            overflow: "hidden",
          }}
        >
          {/* Background Gradients */}
          <Box
            sx={{
              position: "absolute",
              top: "-20%",
              left: "-10%",
              width: "60vw",
              height: "60vw",
              background:
                "radial-gradient(circle, rgba(79,70,229,0.1) 0%, rgba(248,250,252,0) 70%)",
              filter: "blur(60px)",
              zIndex: 0,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: "-20%",
              right: "-10%",
              width: "50vw",
              height: "50vw",
              background:
                "radial-gradient(circle, rgba(59,130,246,0.1) 0%, rgba(248,250,252,0) 70%)",
              filter: "blur(60px)",
              zIndex: 0,
            }}
          />

          {/* Left Content */}
          <Box sx={{ maxWidth: 540, position: "relative", zIndex: 1 }}>
            <Typography
              variant="h3"
              fontWeight={800}
              sx={{ mb: 2, minHeight: 70 }}
            >
              <TypeAnimation
                sequence={[
                  "Payroll — Sorted.",
                  1300,
                  "Receipts — Organised.",
                  1300,
                  "Invoices — Tracked.",
                  1300,
                  "Admin — Automated.",
                  2000,
                ]}
                wrapper="span"
                speed={48}
                repeat={Infinity}
                style={{ display: "inline-block" }}
              />
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: "text.secondary", mb: 3, lineHeight: 1.5 }}
            >
              Stop dreading admin day. <strong>SortlyBooks</strong> lets you
              upload receipts, track payroll, and send invoices — all in one
              simple dashboard. Designed for freelancers and small business
              owners who want their evenings back.
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              sx={{ position: "relative", zIndex: 1 }}
            >
              <Button
                variant="contained"
                color="primary"
                size="large"
                sx={{
                  borderRadius: 2,
                  px: 4,
                  boxShadow: "0 4px 14px 0 rgba(79,70,229,0.39)",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px rgba(79,70,229,0.23)",
                  },
                }}
                component={RouterLink}
                to="/signup"
              >
                Try Free for 14 Days
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  borderRadius: 2,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    backgroundColor: "rgba(79,70,229,0.04)",
                  },
                }}
                component={RouterLink}
                to="/pricing"
              >
                See Pricing
              </Button>
            </Stack>
            <Typography
              variant="caption"
              sx={{ mt: 2, display: "block", color: "text.secondary" }}
            >
              No credit card required. Cancel anytime.
            </Typography>
            <Typography
              variant="caption"
              sx={{
                mt: 1,
                display: "block",
                color: "text.secondary",
                opacity: 0.7,
              }}
            >
              Built in Ireland, shaped by real user feedback.
            </Typography>
          </Box>

          {/* Right Animation */}
          <Box
            sx={{
              width: { xs: "100%", md: 420 },
              mt: { xs: 6, md: 0 },
            }}
          >
            <Lottie animationData={animationData} loop />
          </Box>
        </Box>
        {/* SOCIAL PROOF TICKER */}
        <Box
          sx={{
            bgcolor: "#ffffff",
            py: 3,
            borderTop: "1px solid #f1f5f9",
            borderBottom: "1px solid #f1f5f9",
            overflow: "hidden",
            whiteSpace: "nowrap",
            position: "relative",
            display: "flex",
            "&::before, &::after": {
              content: '""',
              position: "absolute",
              top: 0,
              width: "100px",
              height: "100%",
              zIndex: 2,
            },
            "&::before": {
              left: 0,
              background: "linear-gradient(to right, #ffffff, transparent)",
            },
            "&::after": {
              right: 0,
              background: "linear-gradient(to left, #ffffff, transparent)",
            },
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              paddingLeft: 8,
              animation: "scroll 25s linear infinite",
              "@keyframes scroll": {
                "0%": { transform: "translateX(0)" },
                "100%": { transform: "translateX(-50%)" },
              },
            }}
          >
            {[...Array(2)].map((_, i) => (
              <React.Fragment key={i}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CheckCircleIcon color="success" fontSize="small" /> Trusted
                  by Freelancers
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CheckCircleIcon color="success" fontSize="small" /> 100% GDPR
                  Compliant
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CheckCircleIcon color="success" fontSize="small" /> Built in
                  Europe
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CheckCircleIcon color="success" fontSize="small" /> SSL
                  Secured
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CheckCircleIcon color="success" fontSize="small" /> No Hidden
                  Fees
                </Typography>
              </React.Fragment>
            ))}
          </Box>
        </Box>
        {/* FEATURES */}
        <Box
          sx={{
            py: { xs: 12, md: 16 },
            background: `linear-gradient(to bottom, #f9fbfd, #ffffff)`,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Subtle background pattern */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundImage: "radial-gradient(#e2e8f0 1px, transparent 1px)",
              backgroundSize: "24px 24px",
              opacity: 0.05,
              pointerEvents: "none",
            }}
          />

          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
            <Grid container spacing={10} alignItems="center">
              {/* LEFT — Text */}
              <Grid size={{ xs: 12, md: 5 }}>
                <Typography
                  variant="h4"
                  fontWeight={800}
                  sx={{ mb: 3, lineHeight: 1.22 }}
                >
                  Tidy books. Happy you.
                </Typography>

                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 4 }}
                >
                  SortlyBooks takes the dread out of freelance admin. Everything
                  from receipts to profit tracking — all in one easy, secure
                  dashboard. Designed for modern freelancers and small teams
                  who’d rather be working, not bookkeeping.
                </Typography>

                <Button
                  variant="contained"
                  size="large"
                  component={RouterLink}
                  to="/signup"
                  sx={{
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 4,
                    backgroundColor: "#3b82f6",
                    "&:hover": { backgroundColor: "#2563eb" },
                  }}
                >
                  Start Free – No Card Needed
                </Button>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 2, display: "block", fontStyle: "italic" }}
                >
                  Built in Ireland. Shaped by real user feedback. No credit card
                  required.
                </Typography>
              </Grid>

              {/* RIGHT — Features Grid */}
              <Grid size={{ xs: 12, md: 7 }}>
                <Grid container spacing={4}>
                  {[
                    {
                      icon: <TagIcon />,
                      title: "Effortless Receipt Tagging",
                      desc: "Snap or upload receipts in seconds — auto-tagged for you. No more shoeboxes.",
                      bg: "#dcfce7",
                    },
                    {
                      icon: <SearchIcon />,
                      title: "Lightning-Fast Search",
                      desc: "Find any invoice, receipt, or report in a flash. No more endless scrolling.",
                      bg: "#ede9fe",
                    },
                    {
                      icon: <BarChartIcon />,
                      title: "One-Click Accountant Reports",
                      desc: "Export tidy, accountant-ready reports whenever you need them — zero spreadsheet skills required.",
                      bg: "#fef9c3",
                    },
                    {
                      icon: <ReceiptLongIcon />,
                      title: "Branded Invoice Sending",
                      desc: "Send professional PDF invoices with your logo and details, straight from your dashboard.",
                      bg: "#dbeafe",
                    },
                    {
                      icon: <FolderOpenIcon />,
                      title: "Project & Profit Tracking",
                      desc: "See profit, expenses, and invoices by project or client — finally, know what’s working.",
                      bg: "#e0f2fe",
                    },
                  ].map((f, i) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={i}>
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <Box
                          sx={{
                            backgroundColor: f.bg,
                            width: 48,
                            height: 48,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {React.cloneElement(f.icon, {
                            fontSize: "medium",
                            color: "primary",
                          })}
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={700}>
                            {f.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            {f.desc}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Container>
        </Box>
        {/* APP SCREENSHOT */}
        <Box sx={{ py: 12, backgroundColor: "#ffffff" }}>
          <Container maxWidth="lg">
            <Typography
              variant="h4"
              fontWeight={700}
              textAlign="center"
              sx={{ mb: 2 }}
            >
              See the Dashboard in Action
            </Typography>
            <Typography
              variant="body1"
              textAlign="center"
              color="text.secondary"
              sx={{ mb: 6 }}
            >
              A real view of how SortlyBooks looks inside — designed for
              clarity, not clutter.
            </Typography>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <Box sx={{ position: "relative", mx: "auto", maxWidth: "100%" }}>
                <Box
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                    backgroundColor: "#f9fafb",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  {/* Browser Frame Header */}
                  <Box
                    sx={{
                      height: 40,
                      px: 2,
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: "#f3f4f6",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: "#ef4444",
                        }}
                      />
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: "#f59e0b",
                        }}
                      />
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: "#10b981",
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Dashboard Screenshot */}
                  <Box
                    component="img"
                    src="/screenshots/dashboard-preview.png"
                    alt="SortlyBooks freelancer dashboard preview showing clean interface"
                    sx={{ width: "100%", display: "block" }}
                  />
                </Box>

                {/* Floating Elements */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "15%",
                    right: { xs: 0, md: "-5%" },
                    backgroundColor: "#fff",
                    borderRadius: 4,
                    boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
                    p: 2,
                    display: { xs: "none", sm: "flex" },
                    alignItems: "center",
                    gap: 2,
                    zIndex: 2,
                    animation: "float 6s ease-in-out infinite",
                    border: "1px solid #f1f5f9",
                    "@keyframes float": {
                      "0%, 100%": { transform: "translateY(0)" },
                      "50%": { transform: "translateY(-10px)" },
                    },
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: "#dcfce7",
                      p: 1,
                      borderRadius: "50%",
                      display: "flex",
                    }}
                  >
                    <CheckCircleIcon color="success" />
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={800}
                      color="text.primary"
                    >
                      Invoice Paid
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={500}
                    >
                      €850.00 • Just now
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: "20%",
                    left: { xs: 0, md: "-4%" },
                    backgroundColor: "#fff",
                    borderRadius: 4,
                    boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
                    p: 2,
                    display: { xs: "none", sm: "flex" },
                    alignItems: "center",
                    gap: 2,
                    zIndex: 2,
                    animation: "float 8s ease-in-out infinite reverse",
                    border: "1px solid #f1f5f9",
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: "#e0f2fe",
                      p: 1,
                      borderRadius: "50%",
                      display: "flex",
                    }}
                  >
                    <TagIcon sx={{ color: "#3b82f6" }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={800}
                      color="text.primary"
                    >
                      Receipt Auto-Tagged
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={500}
                    >
                      Uber BV • Travel
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </motion.div>

            <Box sx={{ textAlign: "center", mt: 4 }}>
              <Button
                variant="contained"
                component={RouterLink}
                to="/signup"
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  fontWeight: 600,
                  backgroundColor: "#3b82f6",
                  "&:hover": { backgroundColor: "#2563eb" },
                }}
              >
                Try it Free
              </Button>
            </Box>
          </Container>
        </Box>
        {/* PRICING */}
        <Box sx={{ py: 12, backgroundColor: "#fef9f3" }}>
          <Container maxWidth="lg">
            <Typography
              variant="h4"
              fontWeight={600}
              textAlign="center"
              gutterBottom
            >
              Simple pricing that scales with you
            </Typography>
            <Typography
              variant="body1"
              textAlign="center"
              color="text.secondary"
              sx={{ mb: 6 }}
            >
              Start free — upgrade anytime. No credit card required.
            </Typography>

            <Grid container spacing={4} justifyContent="center">
              {/* Free Plan */}
              <Grid size={{ xs: 12, md: 5 }}>
                <Reveal delay={0.1}>
                  <Paper
                    sx={{
                      p: 4,
                      borderRadius: 3,
                      textAlign: "center",
                      border: "1px solid #e5e7eb",
                      boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
                      transition: "transform 0.2s",
                      "&:hover": { transform: "scale(1.015)" },
                    }}
                  >
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Free Trial
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
                      €0
                    </Typography>

                    <Box sx={{ textAlign: "left", mb: 3 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        ✓ Unlimited uploads for 14 days
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        ✓ Access to all features
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        ✓ Invoicing & payroll included
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        ✓ Smart inbox & filters
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        ✓ Track profit & expenses by project
                      </Typography>

                      <Typography variant="body2" sx={{ mb: 1 }}>
                        ✓ Manual reports & ZIP export
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.disabled"
                        sx={{ mt: 2 }}
                      >
                        — After 14 days, upgrade to continue
                      </Typography>
                    </Box>

                    <Button
                      component={RouterLink}
                      to="/signup"
                      variant="outlined"
                      fullWidth
                      sx={{ borderRadius: "30px", fontWeight: 600 }}
                    >
                      Start Free – No Card Needed{" "}
                    </Button>
                  </Paper>
                </Reveal>
              </Grid>

              {/* Pro Plan */}
              <Grid size={{ xs: 12, md: 5 }}>
                <Reveal delay={0.3}>
                  <Paper
                    sx={{
                      p: 4,
                      borderRadius: 3,
                      textAlign: "center",
                      border: "2px solid #3b82f6",
                      backgroundColor: "#fff",
                      boxShadow:
                        "0 8px 32px rgba(59,130,246,0.15), 0 0 0 4px rgba(59,130,246,0.1)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "scale(1.02) translateY(-4px)",
                        boxShadow:
                          "0 20px 40px rgba(59,130,246,0.2), 0 0 0 6px rgba(59,130,246,0.15)",
                      },
                      position: "relative",
                    }}
                  >
                    <Chip
                      label="Most Popular"
                      color="primary"
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                        fontWeight: 600,
                      }}
                    />
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      gutterBottom
                      color="primary"
                    >
                      Pro
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
                      €29.99/month
                    </Typography>

                    <Box sx={{ textAlign: "left", mb: 3 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        ✓ Unlimited uploads
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        ✓ Auto-send reports to your accountant
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        ✓ Invoicing & payroll tracking
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        ✓ PDF receipt support
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        ✓ VAT breakdown & summaries
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        ✓ ZIP export for accountants
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        ✓ Deadline reminders & alerts
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        ✓ Full project profit & expense tracking with detailed
                        reports
                      </Typography>

                      <Typography variant="body2">
                        ✓ Priority email support
                      </Typography>
                    </Box>

                    <Button
                      variant="contained"
                      fullWidth
                      component={RouterLink}
                      to="/signup"
                      sx={{
                        borderRadius: "30px",
                        fontWeight: 600,
                        minHeight: 48,
                        backgroundColor: "#3b82f6",
                        "&:hover": { backgroundColor: "#2563eb" },
                      }}
                    >
                      Start Free Trial
                    </Button>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 2, display: "block" }}
                    >
                      Cancel anytime · Billed in EUR
                    </Typography>
                  </Paper>
                </Reveal>
              </Grid>
            </Grid>
          </Container>
        </Box>
        <Box sx={{ py: 12, backgroundColor: "#fff" }}>
          <Container maxWidth="lg">
            <Typography
              variant="h4"
              fontWeight={600}
              textAlign="center"
              gutterBottom
            >
              How SortlyBooks Compares
            </Typography>
            <Typography
              variant="body1"
              textAlign="center"
              color="text.secondary"
              sx={{ mb: 6 }}
            >
              See how we stack up against traditional methods
            </Typography>

            <Reveal delay={0.2}>
              <Paper
                sx={{
                  overflowX: "auto",
                  border: "1px solid #e5e7eb",
                  borderRadius: 2,
                }}
              >
                <Table sx={{ minWidth: 650 }}>
                  <TableHead sx={{ backgroundColor: "#f9fafb" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Feature</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>
                        SortlyBooks
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>
                        Spreadsheets
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>
                        Manual Receipts
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Invoice Creation</TableCell>
                      <TableCell align="center">
                        <CheckCircleIcon sx={{ color: "success.main" }} />
                      </TableCell>
                      <TableCell align="center">
                        <CancelIcon sx={{ color: "error.main" }} />
                      </TableCell>
                      <TableCell align="center">
                        <CancelIcon sx={{ color: "error.main" }} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Receipt Storage</TableCell>
                      <TableCell align="center">
                        <CheckCircleIcon sx={{ color: "success.main" }} />
                      </TableCell>
                      <TableCell align="center">
                        <CancelIcon sx={{ color: "error.main" }} />
                      </TableCell>
                      <TableCell align="center">
                        <RemoveCircleIcon sx={{ color: "warning.main" }} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>VAT Reporting</TableCell>
                      <TableCell align="center">
                        <CheckCircleIcon sx={{ color: "success.main" }} />
                      </TableCell>
                      <TableCell align="center">
                        <RemoveCircleIcon sx={{ color: "warning.main" }} />
                      </TableCell>
                      <TableCell align="center">
                        <CancelIcon sx={{ color: "error.main" }} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Email Delivery</TableCell>
                      <TableCell align="center">
                        <CheckCircleIcon sx={{ color: "success.main" }} />
                      </TableCell>
                      <TableCell align="center">
                        <CancelIcon sx={{ color: "error.main" }} />
                      </TableCell>
                      <TableCell align="center">
                        <CancelIcon sx={{ color: "error.main" }} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>
            </Reveal>
          </Container>
        </Box>
        <Box sx={{ py: 12, backgroundColor: "#ffffff" }}>
          <Container maxWidth="lg">
            <Typography
              variant="h4"
              fontWeight={600}
              textAlign="center"
              gutterBottom
            >
              What users are saying
            </Typography>
            <Typography
              variant="body1"
              textAlign="center"
              color="text.secondary"
              sx={{ mb: 6 }}
            >
              Hear from real freelancers and small business owners using
              SortlyBooks
            </Typography>

            <Grid container spacing={4}>
              {testimonials.map((t, i) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                  <Reveal delay={i * 0.1}>
                    <Box
                      sx={{
                        p: 4,
                        backgroundColor: "#f9fafb",
                        borderRadius: 3,
                        border: "1px solid #e5e7eb",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontStyle: "italic",
                          mb: 3,
                          position: "relative",
                          "&::before": {
                            content: "'“'",
                            position: "absolute",
                            left: -16,
                            top: -10,
                            fontSize: "2.5rem",
                            color: "#d1d5db",
                            lineHeight: 1,
                          },
                        }}
                      >
                        {t.quote}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        {t.name}
                      </Typography>
                    </Box>
                  </Reveal>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ textAlign: "center", mt: 6 }}>
              <Typography variant="caption" color="text.secondary">
                100% GDPR Compliant · Built for European freelancers · Your data
                is safe
              </Typography>
            </Box>
          </Container>
        </Box>
        <Box
          sx={{
            py: 12,
            background: "linear-gradient(135deg, #eef2ff 0%, #ffffff 100%)",
            borderTop: "1px solid #e0e7ff",
          }}
        >
          <Container maxWidth="sm" sx={{ textAlign: "center" }}>
            <Reveal>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  backgroundColor: "#dbeafe",
                  color: "#1e40af",
                  px: 2,
                  py: 0.5,
                  borderRadius: "20px",
                  mb: 3,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                }}
              >
                <CheckCircleIcon fontSize="small" /> Last chance to start for
                free
              </Box>
              <Typography
                variant="h3"
                fontWeight={800}
                sx={{ mb: 3, letterSpacing: "-0.02em" }}
              >
                Ready to finally make admin easy?
              </Typography>

              <Button
                variant="contained"
                size="large"
                component={RouterLink}
                to="/signup"
                sx={{
                  mb: 2,
                  px: 5,
                  py: 1.5,
                  backgroundColor: "#3b82f6",
                  fontWeight: 700,
                  fontSize: "1.2rem",
                  borderRadius: "30px",
                  transition: "all 0.3s ease",
                  boxShadow: "0 8px 24px rgba(59,130,246,0.25)",
                  "&:hover": {
                    backgroundColor: "#2563eb",
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 32px rgba(59,130,246,0.35)",
                  },
                }}
              >
                Start Free – No Card Needed
              </Button>

              <Typography
                variant="body2"
                sx={{ mt: 1, color: "text.secondary" }}
              >
                No setup fees. No commitment. Built for freelancers & small
                businesses, with feedback from real users.
              </Typography>
            </Reveal>
          </Container>
        </Box>
        {/* MOBILE APP COMING SOON BANNER */}
        <Box
          sx={{
            py: 7,
            background: "linear-gradient(90deg, #e0f2fe 60%, #f8fafc 100%)",
            textAlign: "center",
            px: 2,
            borderTop: "1px solid #bae6fd",
            borderBottom: "1px solid #bae6fd",
          }}
        >
          <Typography variant="h5" fontWeight={800} sx={{ mb: 1.5 }}>
            📲 SortlyBooks for iPhone
            <br />
            <span style={{ fontWeight: 400, fontSize: "1.1rem" }}>
              Take your business anywhere.
            </span>
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              maxWidth: 520,
              mx: "auto",
              mb: 3,
              fontSize: "1.07rem",
            }}
          >
            Capture receipts in seconds, send invoices from your phone, and keep
            your books up to date wherever you are.
            <br />
            Now available on the App Store — built for freelancers on the move.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            href="https://apps.apple.com/app/6746875594" // Replace with real App Store link
            target="_blank"
            rel="noopener"
            sx={{
              mt: 1,
              px: 4,
              py: 1.3,
              borderRadius: 2,
              fontWeight: 700,
              fontSize: "1rem",
              boxShadow: "0 2px 12px 0 rgba(30,64,175,0.08)",
              letterSpacing: 0.2,
            }}
            startIcon={
              <img
                src="https://www.svgrepo.com/show/503173/apple-logo.svg"
                alt="Download SortlyBooks on the App Store"
                style={{ width: 22, marginRight: 6, verticalAlign: "middle" }}
              />
            }
          >
            Download on the App Store
          </Button>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 2,
              color: "text.secondary",
              opacity: 0.8,
            }}
          >
            Your receipts. Your business. Always with you.
          </Typography>
        </Box>
        {/* FAQ SECTION (insert before Contact CTA) */}
        <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: "#f9fafb" }}>
          <Container maxWidth="md">
            <Typography
              variant="h4"
              fontWeight={800}
              textAlign="center"
              gutterBottom
              sx={{ letterSpacing: -1 }}
            >
              Frequently Asked Questions
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="center"
              sx={{ mb: 5, maxWidth: 520, mx: "auto" }}
            >
              Everything you want to know before getting started.
              <br />
              Can’t find your answer?{" "}
              <span style={{ color: "#2563eb", fontWeight: 600 }}>
                We’re just an email away.
              </span>
            </Typography>

            {faqs.map((item, i) => (
              <Accordion
                key={i}
                sx={{
                  mb: 2.5,
                  borderRadius: 2,
                  boxShadow: "0 2px 12px 0 rgba(59,130,246,0.06)",
                  background: "#fff",
                  "&:before": { display: "none" },
                }}
                disableGutters
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#2563eb" }} />}
                  sx={{ minHeight: 64 }}
                >
                  <Typography fontWeight={700}>{item.q}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography
                    color="text.secondary"
                    sx={{ fontSize: "1.04rem" }}
                  >
                    {item.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Container>
        </Box>
        {/* ACCOUNTANT CTA (commented out for now) */}
        {/* <Box sx={{ py: 12, backgroundColor: "#f9fafb" }}>
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center"> */}
        {/* Left Text Block */}
        {/* <Grid item xs={12} md={6}>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
              👩‍💼 Are you an accountant?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              SortlyBooks makes it easy to receive clean, organized client
              reports each month — no chasing, no email clutter.
            </Typography>
            <ul style={{ paddingLeft: "1.2rem", marginBottom: "2rem" }}>
              <li>✅ View all clients in one dashboard</li>
              <li>✅ Download PDFs and ZIPs instantly</li>
              <li>✅ Invite clients in 1 click</li>
            </ul>
            <Button
              variant="outlined"
              component={RouterLink}
              to="/accountant-signup"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                px: 4,
                py: 1.25,
                borderRadius: 2,
              }}
            >
              Explore the Accountant Portal
            </Button>
          </Grid> */}
        {/* Right Image Preview */}
        {/* <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="/screenshots/accountant-dashboard-preview.png"
              alt="Accountant dashboard preview"
              sx={{
                width: "100%",
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                boxShadow: "0 8px 30px rgba(0,0,0,0.05)",
              }}
            />
          </Grid>
        </Grid>
      </Container>
    </Box> */}
        {/* CONTACT CTA */}
        <Box
          sx={{
            py: 8,
            px: 2,
            backgroundColor: "#f9fbff",
            borderTop: "1px solid #e5e7eb",
            textAlign: "center",
          }}
        >
          <Typography variant="h6" fontWeight={600} gutterBottom>
            💬 Questions? We’re here to help.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Whether you need help getting started or just want to chat — we’re
            happy to hear from you.
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            component={RouterLink}
            to="/contact"
            sx={{ textTransform: "none", px: 4, py: 1 }}
          >
            Contact Us
          </Button>
        </Box>
        {/* FOOTER */}
        <Divider />
        <Box
          component="footer"
          sx={{
            backgroundColor: "#f9fafb",
            py: { xs: 6, md: 8 },
            px: 3,
            mt: 8,
            borderTop: "1px solid #e2e8f0",
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={4} justifyContent="space-between">
              {/* Left: Brand & Tagline */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, mb: 1, color: "primary.main" }}
                >
                  SortlyBooks
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ maxWidth: 360 }}
                >
                  Built for freelancers and small business owners who hate
                  admin. Automate your receipts, invoices, and reports in
                  minutes.
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 2, display: "block" }}
                >
                  © {new Date().getFullYear()} SortlyBooks. All rights reserved.
                </Typography>
              </Grid>

              {/* Right: Links */}
              <Grid
                size={{ xs: 12, md: 6 }}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 4,
                  justifyContent: { xs: "flex-start", md: "flex-end" },
                  mt: { xs: 4, md: 0 },
                }}
              >
                <Grid
                  size={{ xs: 12, md: 6 }}
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 4,
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                    mt: { xs: 4, md: 0 },
                  }}
                >
                  {/* Product Links */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      Product
                    </Typography>
                    <Link
                      component={RouterLink}
                      to="/pricing"
                      underline="none"
                      color="text.secondary"
                      display="block"
                      sx={{ "&:hover": { color: "primary.main" } }}
                    >
                      Pricing
                    </Link>
                    <Link
                      component={RouterLink}
                      to="/blog"
                      underline="none"
                      color="text.secondary"
                      display="block"
                      sx={{ "&:hover": { color: "primary.main" } }}
                    >
                      Blog
                    </Link>
                    <Link
                      component={RouterLink}
                      to="/signup"
                      underline="none"
                      color="text.secondary"
                      display="block"
                      sx={{ "&:hover": { color: "primary.main" } }}
                    >
                      Get Started
                    </Link>
                    <Link
                      component={RouterLink}
                      to="/login"
                      underline="none"
                      color="text.secondary"
                      display="block"
                      sx={{ "&:hover": { color: "primary.main" } }}
                    >
                      Login
                    </Link>
                  </Box>

                  {/* Legal Links */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      Legal
                    </Typography>
                    <Link
                      component={RouterLink}
                      to="/privacy"
                      underline="none"
                      color="text.secondary"
                      display="block"
                      sx={{ "&:hover": { color: "primary.main" } }}
                    >
                      Privacy Policy
                    </Link>
                    <Link
                      component={RouterLink}
                      to="/terms"
                      underline="none"
                      color="text.secondary"
                      display="block"
                      sx={{ "&:hover": { color: "primary.main" } }}
                    >
                      Terms of Service
                    </Link>
                  </Box>

                  {/* Social Links */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      Connect
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Link
                        href="https://twitter.com/sortlybooks"
                        target="_blank"
                        rel="noopener"
                        color="text.secondary"
                        sx={{ "&:hover": { color: "primary.main" } }}
                      >
                        <TwitterIcon fontSize="medium" />
                      </Link>
                      <Link
                        href="https://instagram.com/sortlybooks"
                        target="_blank"
                        rel="noopener"
                        color="text.secondary"
                        sx={{ "&:hover": { color: "primary.main" } }}
                      >
                        <InstagramIcon fontSize="medium" />
                      </Link>
                      <Link
                        href="https://facebook.com/sortlybooks"
                        target="_blank"
                        rel="noopener"
                        color="text.secondary"
                        sx={{ "&:hover": { color: "primary.main" } }}
                      >
                        <FacebookIcon fontSize="medium" />
                      </Link>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
    </>
  );
}
